/**
 * Timezone helpers shared by the routine card, the diff engine, the draft
 * sanitizer and the Momento schedule tool. Pure module: no I/O.
 */

/**
 * A zone is acceptable only if it is a canonical IANA Area/Location name.
 * UTC is allowed explicitly (it is a real, unambiguous choice and appears as
 * the scheduler's own fallback) even though supportedValuesOf omits it.
 */
export function isCanonicalTimeZone(tz: string): boolean {
  if (tz === 'UTC') return true;
  try {
    const supported = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] })
      .supportedValuesOf?.('timeZone');
    if (supported) return supported.includes(tz);
    // Very old runtime: fall back to shape + parseability rather than nothing.
    if (!tz.includes('/')) return false;
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * The city half of an IANA zone, in plain words: "America/Toronto" → "Toronto",
 * "America/Argentina/Buenos_Aires" → "Buenos Aires", "UTC" → "UTC". Used on the
 * card ("Toronto time") — a visitor never reads "America/Toronto".
 */
export function tzCity(tz: string | null | undefined): string {
  if (!tz) return '';
  const trimmed = tz.trim();
  if (!trimmed) return '';
  if (trimmed === 'UTC' || trimmed === 'Etc/UTC') return 'UTC';
  const last = trimmed.split('/').pop() ?? trimmed;
  return last.replace(/_/g, ' ');
}

/**
 * How a confirmation names the zone: "America/Whitehorse" → "Whitehorse time",
 * "America/New_York" → "New York time", UTC/GMT → "UTC" (the voice agent's
 * zone_word). '' for a key with no city ("Etc/GMT+5", "EST5EDT"), so the
 * caller keeps its own label.
 */
export function zoneTimeLabel(tz: string | null | undefined): string {
  const key = (tz ?? '').trim();
  if (!key) return '';
  if (['UTC', 'ETC/UTC', 'GMT', 'ETC/GMT'].includes(key.toUpperCase())) return 'UTC';
  if (!key.includes('/') || /^etc\//i.test(key)) return '';
  const city = tzCity(key);
  return city ? `${city} time` : '';
}
